import { arrangeQuestionRows } from './questionRows';
import {planPages} from './pageLayout';
import PaginationExtension from 'tiptap-extension-pagination';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';

// Measure editable blocks at their printed A4 width. Unlike blank-line page
// breaks, this responds to actual text, diagram heights and working space.
export const WorksheetPagination = PaginationExtension.extend({
  addProseMirrorPlugins() {
    const inherited = this.parent?.() || [];
    return [...inherited.filter(p => !p.key.startsWith('pagination$')), new Plugin({
      key: new PluginKey('worksheetPagination'),
      view(view) {
        let frame;
        let stopped = false;
        const layout = () => {
          if (stopped || view.composing) return;
          const { doc, schema, selection } = view.state;
          const blocks = [];
          const collect = (node, pos) => {
            if (node.type.name === 'doc' || node.type.name === 'page' || node.type.name === 'body') {
              node.forEach((child, offset) => collect(child, pos + offset + (node.type.name === 'doc' ? 0 : 1)));
            } else if (node.type.name !== 'header-footer') blocks.push({ node, pos });
          };
          collect(doc, 0);
          if (!blocks.length) return;
          const clean = view.dom.closest('.worksheet-workspace')?.dataset.style === 'clean';
          const current = blocks.map(b => b.node);
          const arranged = arrangeQuestionRows(current, nodes => schema.nodes.questionRow.create(null, nodes), clean);
          if (current.length !== arranged.length || current.some((node, i) => !node.eq(arranged[i]))) {
            // Keep the selection inside its question when grouping or ungrouping rows.
            let anchor;
            doc.descendants((node, pos) => { if (node.type.name === 'questionBlock' && selection.from >= pos && selection.from < pos + node.nodeSize) anchor = { node, offset: selection.from - pos }; });
            const next = schema.nodes.doc.create(null, schema.nodes.page.create(null, schema.nodes.body.create(null, arranged)));
            const tr = view.state.tr.replaceWith(0, doc.content.size, next.content).setMeta('addToHistory', false);
            if (anchor) next.descendants((node, pos) => { if (node === anchor.node) tr.setSelection(TextSelection.near(tr.doc.resolve(pos + anchor.offset))); });
            view.dispatch(tr); return;
          }

          const scale = view.dom.closest('.editor-zoom-layer')?.getBoundingClientRect().width / view.dom.closest('.editor-zoom-layer')?.offsetWidth || 1;
          const pages = [];

          const firstPage = doc.firstChild?.type.name === 'page' ? doc.firstChild : null;
          const pageAttrs = firstPage?.attrs;
          const cursorBlock = blocks.find(b => selection.from >= b.pos && selection.from <= b.pos + b.node.nodeSize);
          const measuredBlocks=blocks.map(({node,pos})=>{
            const el = view.nodeDOM(pos);
            const style = el instanceof HTMLElement ? getComputedStyle(el) : null;
            const measured = el?.getBoundingClientRect?.().height / scale || 24;
            const blockHeight = measured + (parseFloat(style?.marginTop) || 0) + (parseFloat(style?.marginBottom) || 0);
            return {node,pos,height:blockHeight,startAnswerKey:node.type.name==='answerSection'&&node.attrs.start};
          });
          const {pages:groups,oversized}=planPages(measuredBlocks);
          groups.forEach(group=>pages.push(schema.nodes.page.create(pageAttrs,schema.nodes.body.create(null,group.map(b=>b.node)))));
          const elements=new Set(blocks.map(b=>view.nodeDOM(b.pos)).filter(el=>el instanceof HTMLElement));
          for(const el of observed)if(el!==view.dom&&!elements.has(el)){observer.unobserve(el);observed.delete(el);}
          for(const el of elements)if(!observed.has(el)){observer.observe(el);observed.add(el);}
          view.dom.dispatchEvent(new CustomEvent('worksheet-overflow', { bubbles: true, detail: oversized }));
          const next = schema.nodes.doc.create(null, pages);
          if (next.eq(doc)) return;
          const tr = view.state.tr.replaceWith(0, doc.content.size, next.content).setMeta('addToHistory', false);
          if (cursorBlock) {
            let target;
            next.descendants((node, pos) => {
              if (target === undefined && node === cursorBlock.node) target = pos + (selection.from - cursorBlock.pos);
            });
            if (target !== undefined) tr.setSelection(TextSelection.near(tr.doc.resolve(Math.min(target, tr.doc.content.size))));
          }
          view.dispatch(tr);
        };
        const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(layout); };
        const observer = new ResizeObserver(schedule);
        const observed=new Set([view.dom]);
        observer.observe(view.dom);
        const workspace = view.dom.closest('.worksheet-workspace');
        const attributes = new MutationObserver(schedule);
        if (workspace) attributes.observe(workspace, { attributes: true, attributeFilter: ['data-style', 'data-layout', 'data-working'] });
        document.fonts?.ready.then(() => { if (!stopped) schedule(); });
        schedule();
        return { update: schedule, destroy: () => { stopped = true; cancelAnimationFrame(frame); observer.disconnect(); attributes.disconnect(); } };
      },
    })];
  },
});
